import { forwardRef, useImperativeHandle, useState } from 'react'

import { Cards, LoadingTurbo } from 'atoms'
import SkeletonCard from 'atoms/Cards/SkeletonCard'
import { LinearGradient } from 'expo-linear-gradient'
import {
  Center,
  Box,
  VStack,
  useTheme,
  ZStack,
  Text,
  View,
  HStack,
  Heading,
  useBreakpointValue
} from 'native-base'
import { useWindowDimensions } from 'react-native'
import { formatDateStringToMask } from 'src/utils'
import { LogEvent } from 'src/utils/logEvents'

import { useListActivities } from './hook'
import { ListTypesRef, ListTypesView } from '../../OrganizationScreen.types'

export const ListActivities = forwardRef<ListTypesRef, ListTypesView>(
  ({ navigation, isLoading }, ref) => {
    const [search, setSearch] = useState('')

    const isMobile = useBreakpointValue({ base: true, lg: false })

    const {
      list,
      handleComplete,
      isFetchingNextPage,
      isLoading: localIsLoading,
      refreshing,
      fetchMore,
      refetch,
      totalItens
    } = useListActivities({
      search
    })

    const skeletonArray = new Array(6).fill(0)

    useImperativeHandle(ref, () => ({
      refreshing,
      fetchMore,
      refetch,
      search: (value: string) => {
        setSearch(value)
      },
      clearSearch: () => setSearch('')
    }))

    const { width, height } = useWindowDimensions()

    const theme = useTheme()

    return (
      <Box mt={10} position="relative" px={4}>
        {localIsLoading && (
          <VStack py={4} w={width - 32}>
            {skeletonArray.map((_, i) => (
              <SkeletonCard key={`key-skeleton-${i}`} />
            ))}
          </VStack>
        )}

        {!list?.items.length && !localIsLoading && (
          <Box mt={6} alignItems="center" justifyContent="center">
            <Text fontSize="md" lineHeight="24px" color="gray.500">
              Ainda não há tarefas na sua organização.
            </Text>
          </Box>
        )}

        <HStack justifyContent="space-between" mb={4}>
          {!isLoading && !localIsLoading && !!list?.items.length && (
            <Heading fontWeight="bold" lineHeight={{ base: '28px', lg: '38px' }} fontSize="24px">
              {isMobile ? 'Todas' : 'Todas as tarefas'}
            </Heading>
          )}

          {!isLoading && !localIsLoading && !!list?.items.length && (
            <View>
              <Box
                flexDir="row"
                bgColor={{ base: 'primary.800', lg: undefined }}
                px={3}
                py={1}
                borderRadius={20}>
                {isMobile ? (
                  <Heading color="white" fontSize={12} fontWeight="bold" fontFamily="heading">
                    {totalItens} {`Tarefa${Number(totalItens) > 1 ? 's' : ''}`}
                  </Heading>
                ) : (
                  <Text color="gray.400" fontSize="16px" fontWeight="normal">
                    {totalItens} {`Tarefa${Number(totalItens) > 1 ? 's' : ''}`}
                  </Text>
                )}
              </Box>
            </View>
          )}
        </HStack>

        {!isLoading &&
          list?.items.map((activity, i) => (
            <Box mb={2} key={`key-${i}-${activity.title}`}>
              <Cards.OrganizationCard
                expandedDetail
                isLoadingFavorite={isLoading}
                cover={activity.store?.logo_image?.url}
                topText={activity?.title}
                organizationType={activity?.activity_type?.name}
                nameFantasy={activity?.store?.name_fantasy}
                tabNumber={activity?.store?.tab_number}
                midText={
                  activity?.store?.tab_number
                    ? `TAB ${activity?.store?.tab_number}`
                    : 'Minha tarefa'
                }
                bottomText={
                  activity?.period_infos?.schedule_date_start &&
                  activity?.period_infos?.schedule_date_end
                    ? `${formatDateStringToMask(
                        activity?.period_infos?.schedule_date_start,
                        'dd/MM/yyyy'
                      )} até ${formatDateStringToMask(
                        activity?.period_infos?.schedule_date_end,
                        'dd/MM/yyyy'
                      )}`
                    : activity.schedule_date
                    ? formatDateStringToMask(activity?.schedule_date, 'dd/MM/yyyy')
                    : undefined
                }
                hour={formatDateStringToMask(activity?.schedule_date, 'HH:mm')}
                attachments={activity?.count_activity_attachments}
                planActions={activity?.activity_plan_action}
                userName={activity?.user_name}
                active={activity?.is_finished}
                icon="Check"
                onPress={() => {
                  LogEvent('atividade', {
                    item_id: activity?.id
                  })

                  navigation.navigate('OrganizationActivity', {
                    id: String(activity?.id)
                  })
                }}
                onCheckPress={(callback, currentCheck) =>
                  handleComplete(callback, currentCheck, activity.id)
                }
              />
            </Box>
          ))}

        {isFetchingNextPage && (
          <Center mt={6}>
            <LoadingTurbo size={48} />
          </Center>
        )}

        {refreshing && !isLoading && (
          <>
            <LinearGradient
              style={{
                position: 'absolute',
                height: 800,
                zIndex: 4,
                flex: 1,
                width
              }}
              colors={['#ffffff05', theme.colors.background.light, '#ffffff05']}
            />
            <ZStack
              position="absolute"
              w={width}
              h={0}
              alignItems="center"
              justifyContent="center"
              top={height / 5}
              zIndex={8}>
              <Center>
                <LoadingTurbo size={48} />
              </Center>
            </ZStack>
          </>
        )}
      </Box>
    )
  }
)
